import { useState } from 'react';
import {
    Grid, TextField, Button, MenuItem, Typography,
    IconButton, Box, Chip, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import { Form as FormikForm, useFormikContext } from 'formik';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { CustomField } from '../../../inputs/CustomField';
import UploadFilesDrive from '../../../files/UploadFiles';
import getDayjs from '../../../../context/DateInstance';
import { EVENT_TYPES } from './EventsFormValidator';

const CreateEventsForm = ({ onClose }) => {
    const { values, setFieldValue, isSubmitting, errors, touched } = useFormikContext();
    const [newReasonItem, setNewReasonItem] = useState('');

    const addReasonItem = () => {
        const trimmed = newReasonItem.trim();
        if (!trimmed) return;
        setFieldValue('reason_list', [trimmed, ...(values.reason_list || [])]);
        setNewReasonItem('');
    };

    const removeReasonItem = (index) => {
        const updated = (values.reason_list || []).filter((_, i) => i !== index);
        setFieldValue('reason_list', updated);
    };

    const titleLength = (values.reason_title || '').length;

    return (
        <FormikForm>
            <Grid container spacing={2}>

                <Grid item xs={12}>
                    <CustomField
                        name="reason_title"
                        label="Título del evento"
                        fullWidth
                        as={TextField}
                        value={values.reason_title || ''}
                        inputProps={{ maxLength: 80 }}
                        helperText={
                            touched.reason_title && errors.reason_title
                                ? errors.reason_title
                                : `${titleLength}/80 caracteres`
                        }
                        error={touched.reason_title && Boolean(errors.reason_title)}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <CustomField
                        name="eventType"
                        label="Tipo de evento"
                        select
                        fullWidth
                        as={TextField}
                        value={values.eventType || ''}
                        error={touched.eventType && Boolean(errors.eventType)}
                        helperText={touched.eventType && errors.eventType}
                    >
                        {EVENT_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                    </CustomField>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <CustomField
                        name="url_button_name"
                        label="Texto del botón (URL)"
                        fullWidth
                        as={TextField}
                        value={values.url_button_name || ''}
                    />
                </Grid>

                <Grid item xs={12}>
                    <CustomField
                        name="url"
                        label="URL del evento"
                        fullWidth
                        as={TextField}
                        value={values.url || ''}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label="Fecha de inicio"
                            value={values.start_date ? getDayjs(values.start_date) : null}
                            onChange={(val) => setFieldValue('start_date', val)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    error: touched.start_date && Boolean(errors.start_date),
                                    helperText: touched.start_date && errors.start_date,
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label="Fecha de fin"
                            value={values.end_date ? getDayjs(values.end_date) : null}
                            onChange={(val) => setFieldValue('end_date', val)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    error: touched.end_date && Boolean(errors.end_date),
                                    helperText: touched.end_date && errors.end_date,
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Imagen del evento</Typography>
                    <UploadFilesDrive
                        files={values.image ? [values.image] : []}
                        onLoadFile={(files) => setFieldValue('image', files[files.length - 1] || null)}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Lista de puntos del evento</Typography>
                    {(values.reason_list || []).length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            {values.reason_list.map((item, index) => (
                                <Chip
                                    key={item}
                                    label={item}
                                    onDelete={() => removeReasonItem(index)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                                />
                            ))}
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Agregar punto..."
                        value={newReasonItem}
                        onChange={(e) => setNewReasonItem(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addReasonItem(); } }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={addReasonItem} size="small">
                                        <AddIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>

                <Grid item xs={12} textAlign="right" mt={1}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button sx={{ ml: 2 }} variant="contained" type="submit" disabled={isSubmitting}>
                        {isSubmitting && <CircularProgress size="15px" sx={{ color: 'white', mr: 1 }} />}
                        {isSubmitting ? 'Guardando...' : 'Guardar'}
                    </Button>
                </Grid>

            </Grid>
        </FormikForm>
    );
};

export default CreateEventsForm;
